/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getEventBus } from 'src/core/utils/eventBus';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { AutoTaggingJobStore } from 'src/customText/core/stores/CustomTextAutoTaggingJobStore';
import { customTextTeachingBubbleMessages } from 'src/customText/core/utils/customTextTeachingBubbleMessages';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type AutoTaggingJobTableCommandBarProps = TableCommandBarProps<AutoTaggingJobStore> & {
    onTriggerAutoTaggingJob: () => void;
    isTriggerAutoTaggingJobDisabled?: boolean;
    isReviewAutotagsDisabled?: boolean;
};

const messages = defineMessages({
    searchPlaceholder: { id: 'customText.autoTaggingJobTableCommandBar.searchPlaceholder', defaultMessage: 'Search' },
    deleteAutoTaggingJob: { id: 'customText.autoTaggingJobTableCommandBar.deleteAutoTaggingJob', defaultMessage: 'Delete' },
    reviewFiles: { id: 'customText.autoTaggingJobTableCommandBar.reviewFiles', defaultMessage: `Review files with auto-labels` },
    startAutoTaggingJob: { id: 'customText.autoTaggingJobTableCommandBar.startAutoTaggingJob', defaultMessage: 'Trigger auto-label' }
});

export const AutoTaggingJobTableCommandBar = observer((props: AutoTaggingJobTableCommandBarProps) => {
    const {
        query = '',
        onQueryChange,
        onTriggerAutoTaggingJob,
        isTriggerAutoTaggingJobDisabled = false,
        isReviewAutotagsDisabled = false
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();
    const history = useHistory();
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const eventBus = getEventBus();

    const reviewAutotagsIdRef = React.useRef<string>(getId());

    eventBus.on('notifyAutotaggingSucceeded', () => {
        createTeachingBubble(
            'CustomTextReviewAutotags',
            localize(customTextTeachingBubbleMessages.reviewAutotagsBody),
            localize(customTextTeachingBubbleMessages.reviewAutotagsTitle),
            `#${reviewAutotagsIdRef.current}`,
            true,
            false
        );
    });

    React.useEffect(() => () => dequeueTeachingBubble('CustomTextReviewAutotags'), []);

    const handleTriggeringAutoTag = () => {
        logButtonClick(serviceName, 'AutolLabeling', `TrigerAutoLabel`, null, rootStore.currentCustomTextProjectStore?.id);
        onTriggerAutoTaggingJob();
    };

    const handleReviewAutotagButtonClick = () => {
        logButtonClick(serviceName, 'AutolLabeling', `ReviewAutolabels`, null, rootStore.currentCustomTextProjectStore?.id);
        history.push(routeTable.customText.dataTaggingPage(rootStore.currentCustomTextProjectStore.id, 'CustomEntityRecognition'), {
            filter: 'autotagged'
        });
    };

    const items: ICommandBarItemProps[] = [
        {
            key: 'startAutoTaggingJob',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="startAutoTaggingJobButtonLanguageToolTip"
                    hidden={canWrite || item.disabled || isTriggerAutoTaggingJobDisabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            role="menuitem"
                            iconProps={{ iconName: 'Add' }}
                            onClick={handleTriggeringAutoTag}
                            text={localize(messages.startAutoTaggingJob)}
                            name={localize(messages.startAutoTaggingJob)}
                            data-automation-id="startAutoTaggingJobButton"
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={
                                !canWrite ||
                                item.disabled ||
                                rootStore.currentCustomTextProjectStore?.isLabelingLocked ||
                                isTriggerAutoTaggingJobDisabled
                            }
                        />
                    }
                />
            )
        },
        {
            key: 'reviewAutotags',
            onRender: (item: ICommandBarItemProps) => (
                <CommandBarButton
                    role="menuitem"
                    data-automation-id="reviewAutotag"
                    iconProps={{ iconName: 'Trackers' }}
                    text={localize(messages.reviewFiles)}
                    onClick={handleReviewAutotagButtonClick}
                    styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                    disabled={item.disabled || rootStore.currentCustomTextProjectStore?.isLabelingLocked || isReviewAutotagsDisabled}
                    id={reviewAutotagsIdRef.current}
                />
            )
        }
    ];

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedSearchBox
                    size={24}
                    value={query}
                    role="menuitem"
                    styles={{ root: { alignSelf: 'center' } }}
                    ariaLabel={localize(messages.searchPlaceholder)}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                />
            )
        }
    ];

    return <LuisCommandBar items={items} query={query} onQueryChange={onQueryChange} farItems={farItems} />;
});
